import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  FileText,
  Image as ImageIcon,
  Trash,
} from "lucide-react";
import jsPDF from "jspdf";
import Breadcrumbs from "../../components/private/Breadcrumbs";
import SubmitLoading from "../../components/common/SubmitLoading";
import api from "../../api";
import {
  apiFetchError,
  apiRequest,
  apiSuccess,
} from "../../redux/api/apiActions";
import { showAlert } from "../../redux/alert/alertActions";
import { arClass } from "../../utils/useArClass";
import textTranslator from "../../utils/useTextTranslator";
import formatDate from "../../utils/formatDate";
import formatTime from "../../utils/formatTime";
import CustomSelect from "../../components/CustomSelect";
import AppointmentOverviewCard from "../../components/private/appointments/AppointmentById/AppointmentOverviewCard";
import AppointmentPatientCard from "../../components/private/appointments/AppointmentById/AppointmentPatientCard";
import AppointmentProviderCard from "../../components/private/appointments/AppointmentById/AppointmentProviderCard";
import AppointmentNotesCard from "../../components/private/appointments/AppointmentById/AppointmentNotesCard";
import AppointmentTimestampCard from "../../components/private/appointments/AppointmentById/AppointmentTimestampCard";
import { usePageAccess, checkAccess } from "../../utils/usePageAccess";
import Txt from "../../components/Text";
import DateInput from "../../components/form/DateInput";
import TimeInput from "../../components/form/TimeInput";
import appointmentReasons from "../../utils/constants/visitTypes.json";
import typeTranslator from "../../utils/useTypeTranslator";
const t = (value, page = "APPOINTMENTS") => textTranslator(value, page) || value;
const Text = ({ children, className, page = "APPOINTMENTS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

const Appointment = () => {
  usePageAccess("appointments");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const darkMode = useSelector((state) => state.settings.darkMode);
  const { requests } = useSelector((state) => state.api);
  
  const appointmentKey = `appointment_${id}`;
  const isLoadingAppointment = requests[appointmentKey]?.loading;
const defaultLanguage = useSelector((state) => state.settings.defaultLanguage);
  const [appointment, setAppointment] = useState(null);
  const [editedAppointment, setEditedAppointment] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const colors = useMemo(
    () => ({
      background: darkMode ? "bg-gray-950" : "bg-gray-50",
    }),
    [darkMode],
  );

  const canEdit = checkAccess("putAppointment");
  const canDelete = checkAccess("deleteAppointment");


  const loadAppointment = useCallback(async () => {
    if (!id) {
      dispatch(
        showAlert({
          type: "error",
          title: t("Missing appointment ID"),
          message: t("Appointment ID is required in the URL."),
        }),
      );
      return;
    }

    try {
      dispatch(apiRequest("GET", appointmentKey));
      const res = await api.get(`appointments/${id}`);
      const appointmentData = res?.data || null;

      if (!appointmentData?.id) {
        throw new Error("Appointment not found");
      }

      setAppointment(appointmentData);
      dispatch(apiSuccess("GET", appointmentKey, "Appointment loaded"));
    } catch (error) {
      dispatch(
        apiFetchError(
          "GET",
          appointmentKey,
          error?.response?.data?.error || "Unable to load appointment.",
        ),
      );
      dispatch(
        showAlert({
          type: "error",
          title: t("Load failed"),
          message: error?.response?.data?.error || t("Unable to load appointment."),
        }),
      );
      navigate("/appointments");
    }
  }, [dispatch, navigate, id, appointmentKey]);

  useEffect(() => {
    loadAppointment();
  }, [loadAppointment]);

  useEffect(() => {
    setEditedAppointment(appointment ? { ...appointment } : null);
    setIsEditing(false);
  }, [appointment]);

  const handleEditField = (field, value) => {
    setEditedAppointment((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const handleSaveEdit = async () => {
    if (!id || !editedAppointment) return;

    try {
      setIsSaving(true);
      dispatch(apiRequest("PUT", appointmentKey));
      const res = await api.put(`appointments/${id}`, editedAppointment);
      const updated = res?.data || editedAppointment;
      setAppointment(updated);
      setEditedAppointment(updated);
      setIsEditing(false);
      dispatch(apiSuccess("PUT", appointmentKey, t("updatedMessage", "APPOINTMENTS")));
      dispatch(
        showAlert({
          type: "success",
          title: t("Success", "APPOINTMENTS"),
          message: t("updatedMessage", "APPOINTMENTS"),
        }),
      );
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error || t("updateAppointmentError", "APPOINTMENTS");
      dispatch(apiFetchError("PUT", appointmentKey, errorMessage));
      dispatch(
        showAlert({
          type: "error",
          title: t("Error", "APPOINTMENTS"),
          message: errorMessage,
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedAppointment(appointment ? { ...appointment } : null);
    setIsEditing(false);
  };

  const buildExportData = useCallback(() => {
    if (!appointment) return null;

    return {
      appointmentId: appointment?.id || id || "export",
      patient: appointment?.patient || {},
      provider: appointment?.provider || {},
      appointment,
      raw: appointment,
    };
  }, [appointment, id]);

  const handleExportPDF = useCallback(() => {
    const data = buildExportData();
    if (!data) return;

    const appt = data.appointment || {};
    const patient = data.patient || {};
    const provider = data.provider || {};
    const creator = appt?.creator || {};

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const left = 14;
    const right = pageWidth - 14;
    let y = 16;

    const safe = (value) => {
      if (value === null || value === undefined || value === "") return "-";
      return String(value);
    };

    const fmtDate = (value) => {
      if (!value) return "-";
      return formatDate(value, true);
    };

    const row = (label, value) => {
      doc.setFont(undefined, "bold");
      doc.setFontSize(10);
      doc.text(label, left, y);
      doc.setFont(undefined, "normal");
      doc.text(safe(value), right, y, { align: "right" });
      y += 7;
    };

    doc.setFillColor(41, 98, 165);
    doc.rect(0, 0, pageWidth, 24, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, "bold");
    doc.setFontSize(15);
    doc.text(t("Appointment Details", "APPOINTMENTS"), left, 14);
    doc.setTextColor(0, 0, 0);
    y = 34;

    row(`${t("Appointment", "APPOINTMENTS")} #`, data.appointmentId);
    row(t("Status", "APPOINTMENTS"), t(appt?.status, "APPOINTMENTS"));
    row(t("Appointment Date", "APPOINTMENTS"), fmtDate(appt?.appointmentDate));
    row(t("Appointment Time", "APPOINTMENTS"), safe(appt?.appointmentTime));
    y += 3;
    row(t("Patient", "APPOINTMENTS"), `${safe(patient?.firstName)} ${safe(patient?.lastName)}`.trim());
    row(t("Phone", "APPOINTMENTS"), safe(patient?.phone));
    row(t("Provider", "APPOINTMENTS"), `${safe(provider?.firstName)} ${safe(provider?.lastName)}`.trim());
    row(
      t("Created By", "APPOINTMENTS"),
      `${safe(creator?.firstName)} ${safe(creator?.lastName)} (${safe(textTranslator(creator?.role, "ROLES") || creator?.role)})`.trim(),
    );
    row(t("Created", "APPOINTMENTS"), fmtDate(appt?.createdAt));
    row(t("Last Updated", "APPOINTMENTS"), fmtDate(appt?.updatedAt));
    y += 3;
    row(t("Appointment Notes", "APPOINTMENTS"), appt?.notes || appt?.description || "-");

    doc.save(`appointment-${data.appointmentId}-details.pdf`);
  }, [buildExportData]);

  const handleExportImage = useCallback(() => {
    const data = buildExportData();
    if (!data) return;

    const appt = data.appointment || {};
    const patient = data.patient || {};
    const provider = data.provider || {};
    const creator = appt?.creator || {};

    const safe = (value) => {
      if (value === null || value === undefined || value === "") return "-";
      return String(value);
    };

    const rows = [
      `${t("Appointment", "APPOINTMENTS")} #${safe(data.appointmentId)}`,
      `${t("Status", "APPOINTMENTS")}: ${safe(t(appt?.status, "APPOINTMENTS"))}`,
      `${t("Appointment Date", "APPOINTMENTS")}: ${safe(formatDate(appt?.appointmentDate, true))}`,
      `${t("Appointment Time", "APPOINTMENTS")}: ${safe(appt?.appointmentTime)}`,
      `${t("Patient", "APPOINTMENTS")}: ${safe(patient?.firstName)} ${safe(patient?.lastName)}`,
      `${t("Provider", "APPOINTMENTS")}: ${safe(provider?.firstName)} ${safe(provider?.lastName)}`,
      `${t("Created By", "APPOINTMENTS")}: ${safe(creator?.firstName)} ${safe(creator?.lastName)} (${safe(textTranslator(creator?.role, "ROLES") || creator?.role)})`,
      `${t("Phone", "APPOINTMENTS")}: ${safe(patient?.phone)}`,
      `${t("Created", "APPOINTMENTS")}: ${safe(formatDate(appt?.createdAt, true))}`,
      `${t("Last Updated", "APPOINTMENTS")}: ${safe(formatDate(appt?.updatedAt, true))}`,
      `${t("Appointment Notes", "APPOINTMENTS")}: ${safe(appt?.notes || appt?.description)}`,
    ];

    const canvas = document.createElement("canvas");
    const width = 1400;
    const lineHeight = 38;
    const padding = 56;
    const height = padding * 2 + rows.length * lineHeight + 40;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = "#2962a5";
    ctx.fillRect(0, 0, width, 86);

    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 42px Arial";
    ctx.fillText(t("Appointment Details", "APPOINTMENTS"), padding, 56);

    ctx.fillStyle = "#111827";
    ctx.font = "24px Arial";
    let yText = 130;
    rows.forEach((line, index) => {
      if (index === 0) {
        ctx.font = "bold 28px Arial";
      } else {
        ctx.font = "24px Arial";
      }
      ctx.fillText(line, padding, yText);
      yText += lineHeight;
    });

    const image = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = image;
    link.download = `appointment-${data.appointmentId}-details.png`;
    link.click();
  }, [buildExportData]);

  const handleDelete = () => {
    dispatch(
      showAlert({
        id: `delete-confirmation-${appointment.id}`,
        type: "confirmation",
        title: t("Delete Appointment"),
        message: `${t("Are you sure you want to delete appointment")} #${appointment.id}? ${t("This action cannot be undone.")}`,
        onConfirm: () => confirmDelete(appointment),
      })
    );
  };

  const confirmDelete = async (apt) => {
    try {
      dispatch(apiRequest("DELETE", appointmentKey));
      await api.delete(`appointments/${id}`);

      dispatch(
        apiSuccess(
          "DELETE",
          appointmentKey,
          t("Appointment deleted")
        )
      );

      dispatch(
        showAlert({
          type: "success",
          title: t("Deleted"),
          message: `${t("appointment")} #${apt.id} ${t("deleted successfully.")}`,
        })
      );
      navigate("/appointments");
    } catch (error) {
      const errorMessage = error?.response?.data?.error || t("Unable to delete appointment.");
      dispatch(apiFetchError("DELETE", appointmentKey, errorMessage));
      dispatch(
        showAlert({
          type: "error",
          title: t("Error"),
          message: errorMessage,
        })
      );
    }
  };
 const statusOptions = [
    { label: textTranslator("Scheduled", "APPOINTMENTS"), value: "scheduled" },
    { label: textTranslator("inProgress", "APPOINTMENTS"), value: "in-progress" },
    { label: textTranslator("Completed", "APPOINTMENTS"), value: "completed" },
    { label: textTranslator("Cancelled", "APPOINTMENTS"), value: "cancelled" },
    { label: textTranslator("noShow", "APPOINTMENTS"), value: "no-show" },
  ];
  return (
    <div className={arClass(`min-h-screen pb-24 ${colors.background}`)}>
      {isLoadingAppointment && (
        <SubmitLoading
          message={t("Loading appointment data...", "APPOINTMENTS")}
        />
      )}
      {isSaving && (
        <SubmitLoading
          message={t("updatingAppointment", "APPOINTMENTS")}
        />
      )}

      <Breadcrumbs />

      <main className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {appointment && (
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => navigate("/appointments")}
                className={
                  darkMode
                    ? "inline-flex items-center gap-2 px-1 py-1 text-sm font-medium text-indigo-200 hover:text-indigo-100"
                    : "inline-flex items-center gap-2 px-1 py-1 text-sm font-medium text-indigo-600 hover:text-indigo-700"
                }
              >
                <ArrowLeft className="h-4 w-4" />
                <span><Text>Back to Appointments</Text></span>
              </button>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleExportPDF}
                  className={
                    darkMode
                      ? "inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm font-medium text-white hover:bg-white/10"
                      : "inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
                  }
                >
                  <FileText className="h-4 w-4" />
                  <span><Text>PDF</Text></span>
                </button>
                <button
                  type="button"
                  onClick={handleExportImage}
                  className={
                    darkMode
                      ? "inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm font-medium text-white hover:bg-white/10"
                      : "inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
                  }
                >
                  <ImageIcon className="h-4 w-4" />
                  <span><Text>Image</Text></span>
                </button>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => setIsEditing((prev) => !prev)}
                    className={
                      darkMode
                        ? "inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm font-medium text-white hover:bg-white/10"
                        : "inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
                    }
                  >
                    <Edit className="h-4 w-4" />
                    <span><Text>{isEditing ? "Cancel" : "Edit"}</Text></span>
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className={
                      darkMode
                        ? "inline-flex items-center gap-2 rounded-lg border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-sm font-medium text-rose-300 hover:bg-rose-500/20"
                        : "inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-100 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-200"
                    }
                  >
                    <Trash className="h-4 w-4" />
                    <span><Text>Delete</Text></span>
                  </button>
                )}
              </div>
            </div>

            <AppointmentOverviewCard
              appointment={isEditing ? editedAppointment : appointment}
              formatDate={formatDate}
              formatTime={formatTime}
              darkMode={darkMode}
              arClass={arClass}
            />

            {isEditing && editedAppointment  &&  (
              <div
                className={
                  darkMode
                    ? "rounded-xl border border-white/10 bg-gray-900/50 p-4"
                    : "rounded-xl border border-slate-100 bg-white p-4"
                }
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className={arClass("flex flex-col gap-2")}>
                    <label className={darkMode ? "text-xs text-slate-300" : "text-xs text-slate-600"}>
                    <Text>Status</Text>
                  
                    
                  </label>
                  <CustomSelect
                  options={statusOptions}
                  value={editedAppointment?.status || "scheduled"}
                  onChange={(e) => handleEditField("status", e.target.value)}
                  className={arClass("w-full")}
                  allowCreate={false}
                  allowSearch={false}
                />
                  </div>
                  <div className={arClass("flex flex-col gap-2")}>
                    <label className={darkMode ? "text-xs text-slate-300" : "text-xs text-slate-600"}>
                      <Text>Appointment Date</Text>
                  
                    
                  </label>
                  <DateInput
                                  name="appointmentDate"
                                  value={editedAppointment?.appointmentDate || ""}
                                  onChange={(e) => handleEditField("appointmentDate", e.target.value)}
                                  className={arClass("w-full")}
                                />
                  </div>
                 <div className={arClass("flex flex-col gap-2")}>
                    <label className={darkMode ? "text-xs text-slate-300" : "text-xs text-slate-600"}>
                      <Text>Appointment Time</Text>
                  
                    
                  </label>
                  <TimeInput
                                  name="appointmentTime"
                                  value={editedAppointment?.appointmentTime || ""}
                                  onChange={(e) => handleEditField("appointmentTime", e.target.value)}
                                  className={arClass("w-full")}
                                />
                  </div>

                 <div className={arClass("flex flex-col gap-2")}>
                    <label className={darkMode ? "text-xs text-slate-300" : "text-xs text-slate-600"}>
                      <Text>Reason</Text>
                  
                    
                  </label>
                  <CustomSelect
                                options={appointmentReasons.map((type) => ({
                                  value: type.nameEn,
                                  label:
                                    defaultLanguage === "ar"
                                      ? type.nameAr
                                      : defaultLanguage === "fr"
                                      ? type.nameFr
                                      : type.nameEn,
                                }))}
                                value={typeTranslator(editedAppointment?.appointmentReason).label}
                                onChange={(e) => handleEditField("appointmentReason", e.target.value)}
                                placeholder={textTranslator(
                                  "Select visit type...",
                                  "APPOINTMENTS"
                                )}
                                allowCreate={true}
                                darkMode={darkMode}
                              />
                  </div>

                  <label className="md:col-span-2 text-sm">
                    <span className={darkMode ? "text-xs text-slate-300" : "text-xs text-slate-600"}>
                      <Text>Appointment Notes</Text>
                    </span>
                    <textarea
                      rows={3}
                      value={editedAppointment?.notes || editedAppointment?.description || ""}
                      onChange={(e) => {
                        handleEditField("notes", e.target.value);
                        handleEditField("description", e.target.value);
                      }}
                      className={
                        darkMode
                          ? "mt-1 w-full rounded-lg border border-white/10 bg-slate-900/60 px-3 py-2 text-sm text-white"
                          : "mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
                      }
                    />
                  </label>

                  <label className="md:col-span-2 inline-flex items-center gap-2 text-sm mt-1">
                    <input
                      type="checkbox"
                      checked={!!editedAppointment?.isConfirmed}
                      onChange={(e) => handleEditField("isConfirmed", e.target.checked)}
                      className="h-4 w-4"
                    />
                    <span className={darkMode ? "text-slate-200" : "text-slate-700"}>
                      <Text>confirmed</Text>
                    </span>
                  </label>
                </div>

                <div className="mt-4 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSaveEdit}
                    className={
                      darkMode
                        ? "rounded-lg bg-emerald-500/20 px-3 py-1.5 text-xs font-medium text-emerald-200 hover:bg-emerald-500/30"
                        : "rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
                    }
                  >
                    <Text>Save Changes</Text>
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className={
                      darkMode
                        ? "rounded-lg border border-white/10 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-white/10"
                        : "rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
                    }
                  >
                    <Text>Cancel</Text>
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
                <div className="lg:col-span-7 h-full">
                  <AppointmentPatientCard
                    appointment={isEditing ? editedAppointment : appointment}
                    formatDate={formatDate}
                    darkMode={darkMode}
                    arClass={arClass}
                  />
                </div>
                <div className="lg:col-span-5 h-full">
                  <AppointmentTimestampCard
                    appointment={isEditing ? editedAppointment : appointment}
                    formatDate={formatDate}
                    darkMode={darkMode}
                    arClass={arClass}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
                <div className="lg:col-span-7 h-full">
                  <AppointmentNotesCard
                    appointment={isEditing ? editedAppointment : appointment}
                    darkMode={darkMode}
                    arClass={arClass}
                  />
                </div>
                <div className="lg:col-span-5 h-full">
                  {(isEditing ? editedAppointment : appointment)?.provider && (
                    <AppointmentProviderCard
                      appointment={isEditing ? editedAppointment : appointment}
                      darkMode={darkMode}
                      arClass={arClass}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Appointment;